/** Mirrors shopexperts-api config/media.php `project_attachments` (direct chat reuses it): 15360 KB. */
export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

export const ATTACHMENT_EXTENSIONS = [
  "jpg", "jpeg", "png", "gif", "webp", "pdf", "doc", "docx", "xls", "xlsx",
  "ppt", "pptx", "rtf", "odt", "ods", "odp", "csv", "txt", "zip",
] as const;

export const ATTACHMENT_ACCEPT = ATTACHMENT_EXTENSIONS.map(extension => `.${extension}`).join(",");

const SUPPORTED_TYPES = "images, PDF, Word, Excel, PowerPoint, OpenDocument, CSV, TXT, RTF, or ZIP";

export type AttachmentProblem = "too_large" | "unsupported" | "rejected" | "network" | "failed";

export interface AttachmentFailure {
  problem: AttachmentProblem;
  /** Short chip label. */
  label: string;
  /** Full sentence naming the file. */
  reason: string;
}

export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

const LIMIT = formatFileSize(MAX_ATTACHMENT_BYTES);

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
}

function tooLarge(file: File): AttachmentFailure {
  if (file.size <= MAX_ATTACHMENT_BYTES) {
    return { problem: "too_large", label: "Too large", reason: `${file.name} is ${formatFileSize(file.size)}, which is more than the server accepts right now. Try a smaller file.` };
  }
  return { problem: "too_large", label: "Too large", reason: `${file.name} is ${formatFileSize(file.size)}. Files can be up to ${LIMIT}.` };
}

/** Problems detectable before uploading, or null when the file may be sent. */
export function checkAttachment(file: File): AttachmentFailure | null {
  if (file.size > MAX_ATTACHMENT_BYTES) return tooLarge(file);
  if (!(ATTACHMENT_EXTENSIONS as readonly string[]).includes(extensionOf(file.name))) {
    return { problem: "unsupported", label: "Not supported", reason: `${file.name} isn't a supported file type. Use ${SUPPORTED_TYPES}.` };
  }
  return null;
}

interface HttpLikeError {
  message?: string;
  code?: string;
  response?: { status?: number; data?: { message?: unknown; errors?: Record<string, unknown> } };
}

function serverReason(error: HttpLikeError): string | null {
  const data = error.response?.data;
  const fieldErrors = data?.errors ? Object.entries(data.errors).find(([key]) => key.startsWith("files")) : undefined;
  const fieldMessage = Array.isArray(fieldErrors?.[1]) ? fieldErrors[1][0] : fieldErrors?.[1];
  const message = typeof fieldMessage === "string" && fieldMessage.trim() ? fieldMessage : data?.message;
  if (typeof message !== "string" || !message.trim()) return null;
  if (/^media validation failed$|^the given data was invalid/i.test(message.trim())) return null;
  return message.trim().replace(/\bthe files\.\d+( field)?\b/i, "The file");
}

/** Explains why the server did not accept `file`. */
export function describeUploadFailure(file: File, error: unknown): AttachmentFailure {
  const http = (error ?? {}) as HttpLikeError;
  const status = http.response?.status;
  if (status === 413) return tooLarge(file);
  if (!http.response && (http.code === "ERR_NETWORK" || /network|timeout|failed to fetch/i.test(http.message ?? ""))) {
    return { problem: "network", label: "Failed", reason: `${file.name} didn't upload because the connection dropped. Try again.` };
  }
  const reason = serverReason(http);
  if (status === 422) {
    if (reason && /failed to upload|greater than|too large|exceeds/i.test(reason)) return tooLarge(file);
    return { problem: "rejected", label: "Not accepted", reason: reason ? `${file.name}: ${reason}` : `${file.name} wasn't accepted. Check it's a supported type under ${LIMIT}.` };
  }
  return { problem: "failed", label: "Failed", reason: reason ? `${file.name} didn't upload: ${reason}` : `${file.name} didn't upload. Try again.` };
}
